// NPM
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { useRecoilState } from 'recoil';

// HELPERS
import { toaster } from 'Helpers/index';

// ANT COMPONENT
import { Divider, Space } from 'antd';
import { CopyOutlined } from '@ant-design/icons';

// STYLE
import "./voucher-deals.scss";

// RECOIL ATOM
import { PlatformVoucherAtom, ProductVoucherAtom } from 'Recoil/voucher.atom';

import 'moment/locale/ms';

interface VoucherDealsComponentProps {
    voucherList: any;
    checkOutPage: boolean;
    type?: string;
    handleApplyVoucher: (applyVoucherInput: HandleVoucherInterface) => void;
    orgId: string;
}

interface HandleVoucherInterface {
  type: string;
  id: string;
  name: string;
  applyBoolean: boolean;
  voucherType: string;
  discountType: string;
  discountValue: string;
  supplierID: string;
  className?: string;
}

const VoucherDeals: React.FC<VoucherDealsComponentProps> = ({voucherList, checkOutPage, handleApplyVoucher, type = "", orgId}) => {

    const { t, i18n } = useTranslation();
    
    const [appliedPlatformVoucher, setAppliedPlatformVoucher] = useRecoilState<any>(PlatformVoucherAtom);
    const [appliedProductVoucher, setAppliedProductVoucher] = useRecoilState<any>(ProductVoucherAtom);

    if (type !== "platform") {
        let sortArr = [...voucherList];
        voucherList = sortArr.sort((a, b) => b.voucherBy.localeCompare(a.voucherBy));
    }
    
    const handleCopyToClipboard = (str: string) => {
        const el = document.createElement('textarea');
        el.value = str;
        el.setAttribute('readonly', '');
        el.style.position = 'absolute';
        el.style.left = '-9999px';
        document.body.appendChild(el);
        el.select();
        document.execCommand('copy');
        document.body.removeChild(el);

        toaster('success', t('toaster.copiedToClipboard'));
    };

    const checkIfApplied = (voucher) => {
        if (type === "platform") {
            return appliedPlatformVoucher.some(e => e.id === voucher._id);
        } else {
            return appliedProductVoucher.some(e => {
                return e.id === voucher._id && e.supplierID === orgId;
            });
        }
    }

    /**
     * Apply voucher when click event triggered.
     * 
     * @param {string}  type           order type
     * @param {object}  voucher        voucher object  
     * @param {boolean} applyBoolean   True to apply
     */
    const applyVoucherFunc = (type, voucher, applyBoolean) => {
        const id = voucher._id;
        const name = voucher.voucherName;
        const voucherType = voucher.voucherType;
        const discountType = voucher.discountType;
        const discountValue = voucher.discountValue;
        let callApplyVoucher = false;
        if (type === "platform") {
            if (appliedPlatformVoucher.length === 0) {
                callApplyVoucher = true;
            } else {
                // APPLY VOUCHER IS DIFFERENT ID
                if (appliedPlatformVoucher[0].id !== id) {
                    callApplyVoucher = true;
                }
            }
            if (callApplyVoucher) {
                handleApplyVoucher({
                    type,
                    id,
                    name,
                    applyBoolean,
                    voucherType,
                    discountValue,
                    discountType,
                    supplierID: orgId
                });
            }
        } else {
            let output = appliedProductVoucher.filter((voucher) => {
                return voucher.supplierID === orgId
            })[0];
            if (output) {
                if (output.id !== voucher._id) {
                    callApplyVoucher = true;
                }
            } else {
                callApplyVoucher = true;
            }
            if (callApplyVoucher) {
                handleApplyVoucher({
                    type,
                    id,
                    name,
                    applyBoolean,
                    voucherType,
                    discountValue,
                    discountType,
                    supplierID: orgId
                })
            }
        }
    }

    const returnLength = (voucherBy) => {
        let count = 0;
        voucherList.map(voucher => {
            if (voucher.voucherBy === voucherBy) {
                count++;
            }
        });
        return count;
    }

    const returnClassName = (index) => {
        let className = "";
        index === voucherList.length - 1 ? className += "pb-8 " : "";
        type === "platform" && index === 0 ? className += "pt-8" : "";
        return className;
    }

    const getFormattedLocaleDate = (dateString: any) => {
        if (i18n.language === 'ms') {
            moment.locale('ms');
        } else {
            moment.locale('en');
        }
        return moment(dateString).format('D MMMM YYYY')
    }

    return (
        <div className="px-16">
            {
                type !== "platform" ? 
                    <div className="pt-8">
                        <div className="grid-tabs">
                            <div className="tab-label">{t("label.shopVouchers")} ({returnLength("SELLER")})</div>
                            <div className="tab-label">{t("label.platformVouchers")} ({returnLength("PLATFORM")})</div>
                        </div>
                        <Divider className="my-8">
                        </Divider>
                    </div>
                    : null
            }
            <div>
                {
                    voucherList.map((voucher, index) => {
                        return (
                            <div key={index} className={returnClassName(index)}>
                                {
                                    type !== "platform" && index === 0 && returnLength("SELLER") > 0 ? 
                                        <div>
                                            <div>
                                                {t("label.shopVouchers")}
                                            </div>
                                            <Divider className="mt-8">
                                            </Divider>
                                        </div> : null
                                }
                                {
                                    type !== "platform" && index === (returnLength("SELLER") > 0 ? returnLength("SELLER"): 0) && returnLength("PLATFORM") > 0 ?
                                        <div>
                                            <div>
                                                {t("label.platformVouchers")}
                                            </div>
                                            <Divider className="mt-8">
                                            </Divider>
                                        </div> : null
                                }
                                <Space>
                                    <div>
                                        {
                                            voucher.discountType === "FIAT" ? 
                                                <div className="bold">RM {voucher.discountValue} {t('label.AmountOff')}</div> :
                                                <div className="bold">{voucher.discountValue}%  {t('label.AmountOff')}</div>
                                        }
                                        <div>{t(`voucher.${voucher.voucherType}`)}</div>
                                        <div>{t('label.MinSpend')} RM{voucher.minAmount ? voucher.minAmount : 0}</div>
                                        {
                                            voucher?.capValue ?
                                                <div>{t('label.CappedAt')} RM{voucher.capValue}</div> :
                                                null
                                        }
                                    </div>
                                    <div>
                                        {
                                            checkOutPage ? 
                                                <div className={`chip ${checkIfApplied(voucher)? "applied": "apply"}`}
                                                onClick={() =>  applyVoucherFunc(type, voucher, true)}>
                                                    {
                                                        checkIfApplied(voucher) ? 
                                                            t('label.Applied'): t('label.Apply')
                                                    }
                                                </div> :
                                                <div className="chip to-copy">
                                                    {voucher.voucherCode}
                                                    <CopyOutlined onClick={() => handleCopyToClipboard(voucher?.voucherCode)} className="copy-icon" />
                                                </div>
                                        }
                                        <div className="mt-10 voucher-usage gray-text">
                                            <span>
                                                {t('label.UseBy')} {getFormattedLocaleDate(voucher.validTo)}.&nbsp;
                                            </span>
                                        </div>
                                    </div>
                                </Space>
                                {
                                    voucherList.length - 1 !== index ?
                                    <Divider className={type !== "platform" && index === returnLength("SELLER") - 1 ? "mb-8" : ""}></Divider> : null
                                }
                            </div>
                        )
                    })
                }
            </div>
        </div>
    )
}

export default VoucherDeals;